import { EventEmitter, inject, Injectable, OnDestroy } from "@angular/core";
import { db } from "../../core/database/db";
import { environment } from "../config/environment";
import DataSyncService from "./data-sync/data-sync.service";
import SyncStatusService, { SyncStatus } from "./sync-status-service";
import { LoggerService } from "../../shared/services/logger.service";
import { UploadManagerService } from "./image-sync/upload-manager.service";
import { filter, firstValueFrom, Subject, takeUntil } from "rxjs";
import { ImageHydrationService } from "./image-sync/image-hydration.service";
import { CustomerCareManagerScheduleService } from "../../domains/jobs/services/customer-care-manager-schedule.service";
import { SwUpdate } from "@angular/service-worker";
import { UiToastService } from "../../shared/components/toast/ui-toast.service";
import { ConstructionManagerScheduleService } from "../../domains/jobs/services/construction-manager-schedule.service";
import { AuthService } from "../authentication/auth.service";
import { UiModalService } from "../../shared/components/modal/ui-modal.service";
import { DataSyncMetadataService } from "./data-sync/data-sync-metadata.service";
import JobDataService from "../../domains/jobs/services/job-data-service";
import { CONSTANTS } from "../../constants";
import { Router } from "@angular/router";

export const lastSyncKey = "BackgroundLastSync";

@Injectable({
  providedIn: "root",
})
export default class SyncManager implements OnDestroy {
  private destroy$ = new Subject<void>();
  private interval: number;
  private timer: number | null = null;
  private isForceSyncInProgress: boolean = false;
  private isAnySyncInProgress: boolean = false;
  private isManualSyncQueued: boolean = false;

  onSyncCompleted: EventEmitter<void> = new EventEmitter();
  onSyncFailed: EventEmitter<void> = new EventEmitter();
  doesSupportServiceWorker: boolean = "serviceWorker" in navigator;
  showOfflineWarning: boolean = true;

  private serviceWorkerUpdateService = inject(SwUpdate);

  constructor(
    private logger: LoggerService,
    private dataSyncService: DataSyncService,
    private syncStatusService: SyncStatusService,
    private customerCareManagerScheduleService: CustomerCareManagerScheduleService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private mediaHydration: ImageHydrationService,
    private imageSyncService: UploadManagerService,
    private toastService: UiToastService,
    private authService: AuthService,
    private modalService: UiModalService,
    private dataSyncMetadataService: DataSyncMetadataService,
    private jobDataService: JobDataService,
    private router: Router
  ) {
    this.customerCareManagerScheduleService.isFetchingSchedule$
      .pipe(
        filter(stillFetching => !stillFetching),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const metadata = this.dataSyncMetadataService.getSyncMetadata();
        if (!!metadata === false) {
          this.logger.infoWithTrace("[SyncManager] No sync metadata found when CCM schedule finished fetching. Skipping cleanup of fully synced jobs.");
          return;
        }
        const fullySyncedJobs = this.dataSyncMetadataService.getSubmittedJobsFullySynced(metadata);
        this.jobDataService.cleanupFullySyncedJobsOlderThanBusinessDays(fullySyncedJobs, 3);
      });
    const syncIntervalMinutes = environment.syncIntervalMinutes;
    this.interval = (syncIntervalMinutes ?? 1) * 60 * 1000;
    this.runSync()
      .then(() => {
        this.dataSyncMetadataService.updateSyncMetadata();
      });

    window.addEventListener("online", async () => {
      this.logger.infoWithTrace("[SyncManager] Network online - triggering manual sync.");
      await this.manuallyTriggerSync();
    });

    this.serviceWorkerUpdateService.unrecoverable.subscribe(event => {
      this.logger.error("[SyncManager] Service Worker update unrecoverable:", event.reason);
      this.clearAndReloadServiceWorker();
    });

    this.serviceWorkerUpdateService.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case "VERSION_DETECTED":
          this.logger.infoWithTrace(`[SyncManager:serviceWorker:versionUpdates:VERSION_DETECTED] Downloading new app version: ${evt.version.hash}`);
          this.toastService.info("A new version of the application is available. Downloading...");
          break;
        case "VERSION_READY":
          this.logger.infoWithTrace(`[SyncManager:serviceWorker:versionUpdates:VERSION_READY] Current app version: ${evt.currentVersion.hash}`);
          this.logger.infoWithTrace(`[SyncManager:serviceWorker:versionUpdates:VERSION_READY] New app version ready for use: ${evt.latestVersion.hash}`);
          this.toastService.info("The version of the application has finished downloading. Reloading...");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          break;
        case "VERSION_INSTALLATION_FAILED":
          this.logger.infoWithTrace(`[SyncManager:serviceWorker:versionUpdates:VERSION_INSTALLATION_FAILED] Failed to install app version '${evt.version.hash}': ${evt.error}`);
          this.logger.infoWithTrace("Clearing cache and unregistering service worker to recover from failed installation.");
          this.clearAndReloadServiceWorker();
          break;
        case "NO_NEW_VERSION_DETECTED":
          this.logger.infoWithTrace("[SyncManager:serviceWorker:versionUpdates:NO_NEW_VERSION_DETECTED] No new app version detected.");
          break;
      }
    });

    if (this.doesSupportServiceWorker) {
      navigator.serviceWorker.ready.then(registration => {
        this.logger.infoWithTrace("[SyncManager:serviceWorker:ready] Service Worker is ready:", registration);
      });
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stop();
  }

  private async clearAndReloadServiceWorker() {
    this.toastService.warn("An error occurred while updating the application. The app will reload to recover.");

    const cacheNames = (await caches?.keys()) || [];
    await Promise.all(cacheNames.map(key => caches.delete(key)));

    const registrations = (await navigator?.serviceWorker?.getRegistrations()) || [];
    await Promise.all(registrations.map(registration => registration.unregister()));

    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  public start(): void {
    if (this.authService.isExternallyAuthenticated()) {
      this.logger.info("[SyncManager:start] External authentication detected. Skipping automatic sync.");
      return;
    }

    this.logger.info(`[SyncManager:start] Starting adaptive sync (wait ${this.interval / 1000}s after each run)`);
    this.scheduleNextSync();
  }

  public stop(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  public hideOfflineWarning(): void {
    this.showOfflineWarning = false;
  }

  private scheduleNextSync(): void {
    this.timer = window.setTimeout(async () => {
      await this.runSync();
      this.scheduleNextSync(); // schedule after completion
    }, this.interval);
  }

  private async runSync(isForceSync = false, sendJobsIndividually = false): Promise<void> {
    let didSync = false;
    try {
      if (this.authService.isExternallyAuthenticated()) {
        this.logger.info("[SyncManager:runSync] External authentication detected. Skipping sync.");
        return;
      }

      if (!navigator.onLine) {
        this.logger.info("[SyncManager:runSync] Offline. Skipping sync.");
        return;
      }

      if (this.isForceSyncInProgress) {
        this.logger.info("[SyncManager:runSync] Force sync already in progress. Skipping new sync request.");
        return;
      }

      if (this.isAnySyncInProgress) {
        this.logger.info("[SyncManager:runSync] Another sync is already in progress. Skipping new sync request.");
        return;
      }

      this.isAnySyncInProgress = true;

      if (isForceSync) {
        this.isForceSyncInProgress = isForceSync;
        this.syncStatusService.addSyncProgressItem("ForceSync:TotalSyncProcess");
        this.syncStatusService.setSyncStatus(SyncStatus.InProgress);
        this.logger.info("[SyncManager:runSync] Starting force sync...");
      }

      this.syncStatusService.addSyncProgressItem("SyncManager:ImageUploadProcess");
      this.imageSyncService.startUpload(isForceSync || sendJobsIndividually)
        .then(async () => {
          this.logger.info("[SyncManager:runSync] Image uploads completed.");
          this.syncStatusService.markSyncProgressItemComplete("SyncManager:ImageUploadProcess");
          if (this.isForceSyncInProgress) {
            this.syncStatusService.setSyncStatus(SyncStatus.InProgress);
          } else if (this.isAnySyncInProgress === false) {
            await this.dataSyncMetadataService.updateSyncMetadata(true);
          }
        })
        .catch(error => {
          this.logger.error("[SyncManager:runSync] Image upload failed.", error);
          this.syncStatusService.markSyncProgressItemComplete("SyncManager:ImageUploadProcess");
        });

      const areUploadsInProgress = this.imageSyncService.areUploadsInProgress();
      const shouldPullDataBeforeSyncing = this.shouldPullDataBeforeSyncing();
      didSync = await this.dataSyncService.startUpload(areUploadsInProgress, shouldPullDataBeforeSyncing, isForceSync, sendJobsIndividually);
      if (didSync) {
        await this.updateLastSyncDate();

        await this.dataSyncMetadataService.updateSyncMetadata();
        this.onSyncCompleted.emit();
      }

      if (isForceSync) {
        this.startForceSyncDownloadInBackground();
      }

      if (isForceSync) {
        this.syncStatusService.markSyncProgressItemComplete("ForceSync:TotalSyncProcess");
      }
      this.isForceSyncInProgress = false;
      this.isAnySyncInProgress = false;
    } catch (error) {

      if (error instanceof Error && error.name === CONSTANTS.ERRORS.SYNC_ABORTED) {
        this.logger.infoWithTrace("[SyncManager:runSync] Sync was aborted.");
        this.isForceSyncInProgress = false;
        this.isAnySyncInProgress = false;


        if (this.isManualSyncQueued) {
          this.logger.infoWithTrace("[SyncManager:runSync] Previous sync aborted. Start queued manual sync.");
          this.isManualSyncQueued = false; // reset the manual sync queued flag to prevent infinite loop
          this.manuallyTriggerSyncInternal(isForceSync, sendJobsIndividually);
        } else {
          // Manual sync is not queued, so we can emit the onSyncCompleted event to indicate that the sync process has completed (even though it was aborted).
          this.logger.infoWithTrace("[SyncManager:runSync] Previous sync aborted. NO queued manual sync. Emitting onSyncCompleted event.");
          this.onSyncCompleted.emit();

          // Since the sync process was aborted and not manually re-queued we can't guarantee the state of the sync status so err on the side of it being incomplete.
          this.syncStatusService.setSyncStatus(SyncStatus.Incomplete);
          this.syncStatusService.flushAllSyncProgressItems();
        }
        return;
      }

      db.handleDatabaseError(error as Error, this.modalService, this.logger);

      this.logger.error("[SyncManager:runSync] Sync failed in Sync Manager.", error);
      this.syncStatusService.setSyncStatus(SyncStatus.Failed);
      this.onSyncFailed.emit();

      if (isForceSync) {
        this.syncStatusService.markSyncProgressItemComplete("ForceSync:TotalSyncProcess");
      }
      this.isForceSyncInProgress = false;
      this.isAnySyncInProgress = false;
    }
  }

  private startForceSyncDownloadInBackground() {
    this.logger.infoWithTrace("[SyncManager:ForceSync] Data upload completed.");
    this.logger.info("[SyncManager:ForceSync] Force pulling data as well");

    (this.authService.isCustomerCareManager()
      ? firstValueFrom(this.customerCareManagerScheduleService.getCustomerCareManagerSchedule())
      : Promise.resolve([]))
      .then(jobs => {
        this.logger.info("[SyncManager:ForceSync] CCM Schedule download complete.");
        return this.mediaHydration.hydrateByJobIds(jobs.map(x => x.id));
      })
      .then(() => {
        this.logger.info("[SyncManager:ForceSync] CCM Media hydration complete.");
        if (this.authService.isConstructionManager()) {
          return firstValueFrom(this.constructionManagerScheduleService.getConstructionManagerSchedule());
        }
        return Promise.resolve([]);
      })
      .then(jobs => {
        this.logger.info("[SyncManager:ForceSync] CM Schedule download complete.");
        return this.mediaHydration.hydrateByJobIds(jobs.map(x => x.id), true);
      })
      .then(() => {
        this.logger.info("[SyncManager:ForceSync] CM Media hydration complete.");
        this.logger.info("[SyncManager:ForceSync] Data download complete.");
      })
      .catch(err => {
        this.logger.error("[SyncManager:ForceSync] Error during forced sync download", err);
      });
  }

  private async updateLastSyncDate(): Promise<void> {
    this.logger.event("SyncManager:UpdateLastSyncRecordStarted", {});

    await db.syncStatus.put({
      id: lastSyncKey,
      lastCompletedSync: new Date()
    });

    this.logger.event("SyncManager:UpdateLastSyncRecordCompleted", {});
  }

  public async manuallyTriggerSync(isForceSync = false, sendJobsIndividually = false): Promise<void> {

    if (this.isAnySyncInProgress) {
      this.logger.infoWithTrace("[SyncManager:manuallyTriggerSync] A sync is already in progress. Abort the current sync and wait before starting a new sync.");
      this.isManualSyncQueued = true;
      this.dataSyncService.abortSync();
    } else {
      return this.manuallyTriggerSyncInternal(isForceSync, sendJobsIndividually);
    }
  }

  private async manuallyTriggerSyncInternal(isForceSync = false, sendJobsIndividually = false): Promise<void> {
    this.isManualSyncQueued = false;;

    // First, stop the current sync interval if it's running
    this.stop();

    // Perform the sync manually
    await this.runSync(isForceSync, sendJobsIndividually);

    // Restart the sync interval with the configured interval time
    this.start();
  }

  private shouldPullDataBeforeSyncing(): boolean {
    const isInAnActiveJobInspection = this.router.url.includes("/job/");
    return isInAnActiveJobInspection === false;
  }

  public async getLastSyncDate(): Promise<Date | null> {
    const lastSync = (await db.syncStatus.get(lastSyncKey))?.lastCompletedSync;
    return lastSync ? new Date(lastSync) : null;
  }
}